import React, { useEffect, useRef } from 'react';
import { useAuthStore } from '@/shared/lib/store';
import { useRefreshToken } from '@/entities/auth';
import { logger } from '@/shared/lib';

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { tokens, updateTokens, clearAuth, setLoading } = useAuthStore();
  const refreshTokenMutation = useRefreshToken();
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const refreshAccessToken = async () => {
    if (!tokens?.refresh_token) {
      clearAuth();
      return;
    }

    try {
      setLoading(true);
      const response = await refreshTokenMutation.mutateAsync({
        body: { refresh_token: tokens.refresh_token },
      });

      if (response.success && response.data) {
        updateTokens(response.data);
        scheduleTokenRefresh(response.data.access_token);
      } else {
        clearAuth();
      }
    } catch (error) {
      logger.error('Token refresh failed:', error);
      clearAuth();
    } finally {
      setLoading(false);
    }
  };

  const scheduleTokenRefresh = (accessToken: string) => {
    try {
      const payload = JSON.parse(atob(accessToken.split('.')[1]));
      const expirationTime = payload.exp * 1000;
      const currentTime = Date.now();
      const timeUntilExpiry = expirationTime - currentTime;
      
      // Refresh 5 minutes before expiry
      const refreshTime = Math.max(timeUntilExpiry - 5 * 60 * 1000, 0);

      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }

      refreshTimeoutRef.current = setTimeout(() => {
        refreshAccessToken();
      }, refreshTime);
    } catch (error) {
      logger.error('Failed to parse token:', error);
      clearAuth();
    }
  };

  useEffect(() => {
    if (tokens?.access_token) {
      scheduleTokenRefresh(tokens.access_token);
    }

    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [tokens?.access_token]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && tokens?.access_token) {
        try {
          const payload = JSON.parse(atob(tokens.access_token.split('.')[1]));
          const expirationTime = payload.exp * 1000;
          const currentTime = Date.now();

          if (currentTime >= expirationTime) {
            refreshAccessToken();
          }
        } catch (error) {
          logger.error('Failed to check token expiry:', error);
          clearAuth();
        }
      }
    };

    // 401 Unauthorized 이벤트 처리
    const handleUnauthorized = () => {
      logger.info('Received unauthorized event, refreshing token...');
      refreshAccessToken();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('auth:unauthorized', handleUnauthorized);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
    };
  }, [tokens?.access_token]);

  return <>{children}</>;
};

AuthProvider.displayName = 'AuthProvider';